import type { EditorOptions } from "@tiptap/core";
import { Blockquote } from "@tiptap/extension-blockquote";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Color } from "@tiptap/extension-color";
import { Document } from "@tiptap/extension-document";
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { HardBreak } from "@tiptap/extension-hard-break";
import { History } from "@tiptap/extension-history";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { Text } from "@tiptap/extension-text";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { useMemo } from "react";
import { FontSize, LinkBubbleMenuHandler } from "mui-tiptap";
import { Heading } from "../../extensions/TipTapHeaderExtension";
import { CollapsableHeading } from "../../extensions/CollapsableHeadingExtension";
import { HighlightMark } from "../../extensions/MarkExtension";
import { TableStyles } from "../../extensions/TableStylesExtension";
import { TableResize } from "../../extensions/TableResizeExtension";
import TableKit from "@tiptap/extension-table";
import Image from "@tiptap/extension-image";

export type UseExtensionsOptions = {
  placeholder?: string;
};
const CustomLinkExtension = Link.extend({
  inclusive: false,
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-tool-link-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-tool-link-id"),
        renderHTML: (attributes) => {
          if (!attributes["data-tool-link-id"]) {
            return {};
          }
          return {
            "data-tool-link-id": attributes["data-tool-link-id"],
          };
        },
      },
      // Keep inline styles for inactive tools
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },
});
const CustomSubscript = Subscript.extend({
  excludes: "superscript",
});

const CustomSuperscript = Superscript.extend({
  excludes: "subscript",
});

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: "width: 100%; height: 100%;",
        parseHTML: (element) =>
          element.getAttribute("style") || "width: 100%; height: 100%;",
        renderHTML: (attributes) => {
          return {
            style: attributes.style || "width: 100%; height: 100%;",
          };
        },
      },
      "data-image-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-image-id"),
        renderHTML: (attributes) => {
          if (!attributes["data-image-id"]) {
            return {};
          }
          return {
            "data-image-id": attributes["data-image-id"],
          };
        },
      },
      "data-version-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-version-id"),
        renderHTML: (attributes) => {
          if (!attributes["data-version-id"]) {
            return {};
          }
          return {
            "data-version-id": attributes["data-version-id"],
          };
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {};
          }
          return {
            title: attributes.title,
          };
        },
      },
    };
  },
  // Allow links on images
  marks: "_",
}).configure({
  inline: true,
  allowBase64: true,
  HTMLAttributes: {
    class: null,
  },
});

export default function useExtensions({
  placeholder,
}: UseExtensionsOptions = {}): EditorOptions["extensions"] {
  return useMemo(() => {
    return [
      TableKit.configure({
        resizable: true,
        lastColumnResizable: false,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TableStyles,
      TableResize.configure({
        cellMinWidth: 27,
        cellMaxWidth: 600,
        rowMinHeight: 27,
        rowMaxHeight: 1000,
      }),
      BulletList,
      Document,
      HardBreak,
      ListItem,
      OrderedList,
      Paragraph,
      CustomSubscript,
      CustomSuperscript,
      Text,
      Bold,
      Blockquote,
      Italic,
      Underline,
      Strike,
      CustomLinkExtension.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      LinkBubbleMenuHandler,
      Gapcursor,
      Heading.configure({
        levels: [2, 3, 4],
      }),
      CollapsableHeading,
      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
        defaultAlignment: "paragraph",
      }),
      TextStyle.configure({ mergeNestedSpanStyles: true }),
      Color,
      FontSize,
      HighlightMark,
      HorizontalRule,
      CustomImage,
      Dropcursor,
      Placeholder.configure({
        placeholder,
      }),
      History,
    ];
  }, [placeholder]);
}
